import { education, experience, profile } from '../../data/site';

const workHabits = [
  'Daily written progress updates',
  'Comfortable on standups in your timezone',
  'Direct with stakeholders, no translation layer needed',
  'Immediate support after release',
];

/*--------------------
* Experience row
----------------------*/
function Role({ role, index }) {
  return (
    <div
      className="timeline-item reveal"
      style={{ transitionDelay: `${index * 0.09}s` }}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <h4 className="font-display text-[17px] font-[600] tracking-[-.01em] text-chalk">
          {role.role}
        </h4>
        {role.current && (
          <span className="chip chip-accent !py-1 font-mono text-[10px] uppercase tracking-[.14em]">
            Now
          </span>
        )}
      </div>

      <div className="mt-1 flex flex-wrap items-center gap-x-2 text-[13.5px] text-muted">
        <span className="text-chalk/80">{role.company}</span>
        <span className="text-tint/20">/</span>
        <span>{role.where}</span>
        <span className="text-tint/20">/</span>
        <span className="font-mono text-[12px] tracking-[.04em] text-tint/50">
          {role.period}
        </span>
      </div>

      <ul className="mt-3.5 space-y-1.5">
        {role.points.map((p) => (
          <li key={p} className="flex items-start gap-2.5 text-[14px] leading-[1.65] text-muted">
            <span className="mt-[9px] h-1 w-1 shrink-0 rounded-full bg-tint/25" />
            {p}
          </li>
        ))}
      </ul>
    </div>
  );
}

/*--------------------
* About Section
----------------------*/
export default function About() {
  return (
    <section id="about" className="section">
      <div className="container">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-14">
          {/* Left: portrait + habits */}
          <div className="lg:col-span-5 lg:sticky lg:top-24 lg:self-start">
            <div className="reveal glass card-sheen overflow-hidden p-3">
              <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-violet-brand/25 via-surface to-cyan-brand/15">
                <div className="grid-lines absolute inset-0 opacity-60" aria-hidden="true" />
                {/* glow behind the cutout */}
                <div
                  className="aurora h-[260px] w-[260px] bg-violet-brand/40 left-1/2 top-6 -translate-x-1/2"
                  aria-hidden="true"
                />
                <img
                  className="relative z-[1] w-full object-cover"
                  loading="lazy"
                  decoding="async"
                  width="720"
                  height="900"
                  src={profile.photo}
                  title={profile.name}
                  alt={profile.name}
                />
                <div
                  className="pointer-events-none absolute inset-x-0 bottom-0 z-[2] h-24 bg-gradient-to-t from-panel to-transparent"
                  aria-hidden="true"
                />
              </div>

              <div className="px-3 pb-2 pt-5">
                <h3 className="font-display text-[19px] font-[600] tracking-[-.01em] text-chalk">
                  {profile.name}
                </h3>
                <p className="mt-1 font-mono text-[11.5px] uppercase tracking-[.14em] text-violet-brand">
                  {profile.role}
                </p>
                <p className="mt-3 text-[14px] text-muted">
                  Based in {profile.location} working remotely with teams across the US,
                  Canada and Europe.
                </p>
              </div>
            </div>

            <div className="reveal glass mt-4 p-6" style={{ transitionDelay: '.1s' }}>
              <h4 className="font-mono text-[11px] uppercase tracking-[.2em] text-muted">
                How I work
              </h4>
              <ul className="mt-4 space-y-2.5">
                {workHabits.map((h) => (
                  <li key={h} className="flex items-start gap-3 text-[14px] text-muted">
                    <span className="mt-0.5 text-cyan-brand" aria-hidden="true">
                      &#10003;
                    </span>
                    {h}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right: story + timeline */}
          <div className="lg:col-span-7">
            <div className="reveal">
              <span className="kicker">About</span>
              <h2 className="section-title mt-4">
                Five years.
                <br className="hidden sm:block" /> One speciality.
              </h2>
              <p className="lede mt-5">
                I&rsquo;ve spent my career on one thing: React Native apps that actually make it
                to production. Consumer and business products, iOS and Android, from
                architecture through App Store review. The last two years have been about
                folding AI into that OpenAI-generated content, computer-vision analysis,
                RAG over product data so the apps do things they couldn&rsquo;t before.
              </p>
            </div>

            <div className="reveal mt-11" style={{ transitionDelay: '.08s' }}>
              <h3 className="font-mono text-[11px] uppercase tracking-[.2em] text-muted">
                Experience
              </h3>
              <div className="mt-7">
                {experience.map((role, i) => (
                  <Role key={`${role.company}-${role.period}`} role={role} index={i} />
                ))}
              </div>
            </div>

            <div className="reveal glass mt-8 p-6" style={{ transitionDelay: '.12s' }}>
              <h3 className="font-mono text-[11px] uppercase tracking-[.2em] text-muted">
                Education
              </h3>
              <div className="mt-4 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                <div>
                  <p className="font-display text-[16px] font-[600] text-chalk">
                    {education.degree}
                  </p>
                  <p className="mt-0.5 text-[14px] text-muted">{education.school}</p>
                </div>
                <span className="font-mono text-[12px] tracking-[.04em] text-tint/50">
                  {education.period}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
